import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { quizRegistry } from "@quizzes/_registry";
import type { Question, QuestionQuiz } from "@quizzes/_schema";
import { isCalculatorQuiz, isGatedQuiz, isQuestionQuiz, isStoryFunnel } from "@quizzes/_schema";
import { StoryFunnelAnalytics } from "../components/StoryFunnelAnalytics";
import {
  DateRangePicker,
  isoDaysAgo,
  isoToday,
  toRangeIso,
} from "../components/DateRangePicker";
import { ConversionHero } from "../components/ConversionHero";
import { DevicesCard } from "../components/DevicesCard";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { ExperimentStatusBar } from "../components/ExperimentStatusBar";
import { PagesTable } from "../components/PagesTable";
import { TimeSeriesChart } from "../components/TimeSeriesChart";
import {
  APPLIED_ARM_ALL,
  APPLIED_ARM_NONE,
  type ArmFilter,
  type FunnelParams,
  type FunnelResponse,
  type ResponseBreakdown,
  api,
  ApiError,
} from "../api";
import { buildResponseReport, reportFilename } from "../utils/report";
import { isRandomizedQuestion, questionLabel } from "../utils/questions";

// Question types that have an option-style response breakdown worth fetching.
const BREAKDOWN_TYPES = new Set(["single", "multi", "slider"]);

// The arm filter, in request order, with the label the operator reads.
// "No arm" is exposure loss -- Intelligems never resolved a test group -- and it
// is deliberately NOT folded into Control: rule 24 keeps them separable because
// that difference is how lost exposure is measured.
const ARMS: { value: ArmFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "control", label: "Control" },
  { value: "variant", label: "Variant" },
  { value: "none", label: "No arm" },
];

// What the API should echo back in `facets.applied.variant` for each request.
// The two rollup sentinels are compared against, never sent.
const APPLIED_FOR: Record<ArmFilter, string> = {
  all: APPLIED_ARM_ALL,
  control: "control",
  variant: "variant",
  none: APPLIED_ARM_NONE,
};

// Shown when an arm was requested and the response says a different one (or
// none) was applied. The cause is almost always deploy skew: `deploy-admin.yml`
// is an S3 sync and `deploy-aws.yml` is a Docker build plus CloudFormation, so
// this bundle goes live minutes before the API that understands it, and FastAPI
// answers an unknown query param by IGNORING it -- HTTP 200, unsplit numbers,
// no error anywhere (rule 23f). Reporting it is the whole reason the API echoes
// what it applied rather than the client trusting what it asked for.
const ARM_NOT_APPLIED =
  "The API did not apply this arm filter — these are UNSPLIT numbers covering " +
  "every arm. The API deploy is probably still rolling out (the admin ships " +
  "first); retry in a few minutes.";

// Why an arm-filtered view has no per-answer rows and no purchase CVR.
const ARM_SCOPE_NOTE =
  "Per-answer breakdowns and purchase CVR are not split by arm. Conversion per " +
  "arm belongs to Intelligems, which owns the assignment and the checkout side.";

// Shown when the funnel `fetch` REJECTS rather than returning a status. The
// browser cannot tell us why: API Gateway's 504 is emitted without CORS
// headers, so a request the backend spent its full 30s on is indistinguishable
// from an offline laptop -- both arrive as an opaque TypeError. The previous
// text ("Failed to load funnel") was accurate and useless; on 2026-08-18 it sat
// over an em-dashed stat row while the real cause, twelve Lambda invocations
// timing out at exactly 30,000ms, was only visible in CloudWatch. Name the
// likely cause and the lever the operator actually has. A real HTTP error
// still reports the server's own detail string -- see the ApiError branch.
const FUNNEL_UNREACHABLE =
  "The funnel request did not respond. This is usually a backend timeout on a " +
  "wide window — try a narrower date range, or check the API logs.";

// Max per-question breakdown requests in flight at once. Each breakdown is a
// live full-window GROUP-BY; running too many at once pegs the small DB's CPU
// and starves the top-line funnel into the 30s gateway timeout (2026-06-19
// "Failed to load funnel" was DB CPU at ~100%, not connection exhaustion).
// Kept low; results still fill in progressively.
const BREAKDOWN_CONCURRENCY = 2;

function questionLabelMap(quiz: QuestionQuiz): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of quiz.questions) out[q.id] = questionLabel(q);
  return out;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  const seconds = Math.round(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("en-US");
}

export function Analytics() {
  const [searchParams] = useSearchParams();
  const definition = quizRegistry[searchParams.get("slug") ?? ""];
  return definition && isStoryFunnel(definition)
    ? <StoryFunnelAnalytics key={definition.slug} story={definition} />
    : <QuestionAnalytics />;
}

function QuestionAnalytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const quizzes = useMemo(() => Object.values(quizRegistry), []);
  const [slug, setSlug] = useState<string>(
    searchParams.get("slug") ?? quizzes[0]?.slug ?? "",
  );
  const [from, setFrom] = useState<string>(isoDaysAgo(7));
  const [to, setTo] = useState<string>(isoToday());
  const [cohort, setCohort] = useState<"all" | "completers" | "abandoners">(
    "all",
  );
  const [persona, setPersona] = useState<string>("");
  const [arm, setArm] = useState<ArmFilter>("all");
  const [denominator, setDenominator] = useState<"answered" | "started">(
    "answered",
  );
  const [data, setData] = useState<FunnelResponse | null>(null);
  // Bumped on every successful funnel response. It is what the panel error
  // boundaries reset on -- see `dataVersion` at its use site for why the filter
  // values are the wrong trigger.
  const [dataVersion, setDataVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-question response breakdowns, fetched lazily (one request per question,
  // in parallel) so the top-line funnel loads fast and no single request
  // computes every breakdown over the full window — that inline pass timed out.
  const [breakdowns, setBreakdowns] = useState<
    Record<string, ResponseBreakdown[]>
  >({});
  const [breakdownsLoading, setBreakdownsLoading] = useState(false);

  const definition = quizRegistry[slug];
  const quiz = definition && isQuestionQuiz(definition) ? definition : undefined;
  // From the quiz DEFINITION, not from the data: a card hidden because the
  // count is zero would hide exactly the gate that is converting at zero.
  const gated = quiz ? isGatedQuiz(quiz) : false;
  // The filter is only meaningful for a quiz that declares an experiment: with
  // none, every session resolves no arm, so "All" and "No arm" are the same set
  // and Control/Variant are empty. The registry is COMPILED into this bundle
  // (rule 8), so a newly-armed quiz needs an admin redeploy before the chips
  // appear -- same staleness the ExperimentStatusBar already lives with.
  const hasExperiment = Boolean(quiz?.experiment);

  const questionTypes = useMemo(() => {
    if (!quiz) return {};
    return Object.fromEntries(quiz.questions.map((q) => [q.id, q.type]));
  }, [quiz]);

  const optionLabels: Record<string, Record<string, string>> = useMemo(() => {
    const out: Record<string, Record<string, string>> = {};
    if (!quiz) return out;
    for (const q of quiz.questions) {
      const labels: Record<string, string> = {};
      if (q.type === "single" || q.type === "multi") {
        for (const o of q.options) labels[o.value] = o.label;
      }
      out[q.id] = labels;
    }
    return out;
  }, [quiz]);

  const randomizedQuestionIds = useMemo(() => {
    const ids = new Set<string>();
    if (!quiz) return ids;
    for (const q of quiz.questions) {
      if (isRandomizedQuestion(q)) ids.add(q.id);
    }
    return ids;
  }, [quiz]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("slug", slug);
    setSearchParams(next, { replace: true });
  }, [slug, searchParams, setSearchParams]);

  // Switching quizzes drops the arm filter. Carrying it across would leave a
  // quiz with no experiment showing an empty Control funnel under a chip the
  // operator can no longer see, which reads as "this quiz got no traffic".
  useEffect(() => {
    if (!hasExperiment) setArm("all");
  }, [slug, hasExperiment]);

  useEffect(() => {
    if (!quiz) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const range = toRangeIso(from, to);
    const params: FunnelParams = {
      slug: quiz.slug,
      from: range.from,
      to: range.to,
      questionIds: quiz.questions.map((q) => q.id),
      questionTypes,
      cohort,
    };
    if (persona) params.persona = persona;
    if (arm !== "all") params.variant = arm;
    api
      .funnel(params)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setDataVersion((v) => v + 1);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message || `HTTP ${err.status}`);
        else setError(FUNNEL_UNREACHABLE);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quiz, from, to, questionTypes, cohort, persona, arm]);

  // Lazily fetch each question's response breakdown AFTER the top-line funnel
  // resolves, with bounded concurrency. Firing the breakdowns alongside the
  // funnel made one page load ~13 concurrent requests, spinning up enough
  // parallel Lambdas to exhaust the DB connection pool -- and the top-line lost
  // the race against its own breakdown siblings ("Failed to load funnel").
  // Gating on `data` (and `!loading`) keeps the top-line from competing for a
  // connection; BREAKDOWN_CONCURRENCY caps the fan-out. Results fill in
  // progressively. Each request is still bounded to one question so none can
  // exceed the request timeout the old all-questions-inline pass did.
  useEffect(() => {
    if (!quiz || loading || !data) return;
    // Not fetched at all under an arm filter, rather than fetched unscoped. The
    // breakdown route takes no arm (its per-question scan is live over raw
    // events, and `intelligems_variant` carries no index), so asking would
    // return an answer distribution over EVERY arm -- which, sitting under step
    // counts filtered to one, reads as though both were filtered.
    if (arm !== "all") {
      setBreakdowns({});
      setBreakdownsLoading(false);
      return;
    }
    let cancelled = false;
    setBreakdowns({});
    const range = toRangeIso(from, to);
    const targets = quiz.questions.filter((q) => BREAKDOWN_TYPES.has(q.type));
    if (targets.length === 0) {
      setBreakdownsLoading(false);
      return;
    }
    setBreakdownsLoading(true);

    const slug = quiz.slug;
    let cursor = 0;
    async function fetchOne(q: Question): Promise<void> {
      try {
        const res = await api.breakdown({
          slug,
          from: range.from,
          to: range.to,
          questionId: q.id,
          questionType: q.type,
          cohort,
          ...(persona ? { persona } : {}),
        });
        if (!cancelled)
          setBreakdowns((prev) => ({ ...prev, [q.id]: res.responses }));
      } catch {
        // Leave this question's breakdown empty; the row still renders.
      }
    }
    async function worker(): Promise<void> {
      while (!cancelled) {
        const idx = cursor++;
        if (idx >= targets.length) return;
        const q = targets[idx];
        if (!q) return;
        await fetchOne(q);
      }
    }
    const pool = Array.from(
      { length: Math.min(BREAKDOWN_CONCURRENCY, targets.length) },
      () => worker(),
    );
    void Promise.all(pool).finally(() => {
      if (!cancelled) setBreakdownsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [quiz, data, loading, from, to, cohort, persona, arm]);

  if (!quiz) {
    return <div className="text-mars-white/60">No quizzes registered.</div>;
  }

  const labels = questionLabelMap(quiz);
  // `== null` rather than `=== undefined`: an API that predates the parameter
  // omits the key entirely, and one mid-rollout could plausibly send null.
  const appliedArm = data?.facets?.applied?.variant ?? null;
  const armNotApplied =
    arm !== "all" && data !== null && appliedArm !== APPLIED_FOR[arm];
  const armed = arm !== "all";
  // The panel boundaries reset on the RESPONSE COUNTER, not on the filter
  // values, and the difference is not cosmetic.
  //
  // A boundary latches by design, so a panel killed by one bad payload needs
  // something to clear it or it stays dead until a full page reload. The
  // obvious key -- `slug|from|to|cohort|persona|arm` -- does not work for the
  // always-mounted panels (Conversion, Devices), because it changes at CLICK
  // time, before the new payload exists: the boundary clears, re-renders the
  // children against the STALE broken data, throws again, and re-latches. The
  // good response then arrives with the key unchanged, and the panel is stuck.
  //
  // Bumping on arrival gets the ordering right, and it is also the more honest
  // trigger: "retry rendering, the data changed" rather than "retry rendering,
  // the user clicked something". A failed fetch does NOT bump it, so a panel
  // stays in its fallback while there is nothing new to render -- correct.
  //
  // The Traffic chart and Pages panels sit behind their loading skeletons, so
  // they are unmounted and remounted on every filter change and would recover
  // without this. They use the same key anyway: relying on a sibling's
  // conditional for error recovery is not a property worth depending on.

  return (
    <div>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="eyebrow">Funnel</span>
          <h1 className="heading mt-1 text-3xl">{quiz.meta.title}</h1>
          <p className="mt-2 text-sm text-mars-white/60">
            Visitors, submits, conversion rate and per-page drop-off.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="eyebrow">Quiz</span>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="mono rounded-md border border-mars-line bg-mars-night px-3 py-1.5 text-sm text-mars-white outline-none transition-colors focus:border-mars-orange"
            >
              {quizzes.map((q) => (
                <option key={q.slug} value={q.slug}>
                  {q.meta.title} ({q.slug})
                </option>
              ))}
            </select>
          </label>
          <DateRangePicker
            from={from}
            to={to}
            onChange={(next) => {
              setFrom(next.from);
              setTo(next.to);
            }}
          />
          <button
            type="button"
            // The report is a per-answer export and there are no per-answer
            // rows under an arm filter, so it would download an answer sheet
            // with nothing on it -- which reads as "this arm answered nothing".
            disabled={!data || breakdownsLoading || armed}
            title={
              armed
                ? "Response breakdowns are not split by arm — switch Arm to All to export"
                : undefined
            }
            onClick={() => {
              if (!data) return;
              const args = {
                quiz,
                from,
                to,
                started: data.started,
                completed: data.completed,
                completionRate: data.completion_rate,
                steps: data.steps,
                breakdowns,
                optionLabels,
                questionTypes,
                randomizedQuestionIds,
                // What the SERVER says it applied, not what this page asked
                // for: an API deployed before a facet existed ignores it
                // silently, and the report must not claim a filter the
                // numbers were never narrowed by.
                cohort: data.facets?.applied?.cohort,
                persona: data.facets?.applied?.persona ?? null,
                generatedOn: new Date().toISOString().slice(0, 10),
              };
              downloadBlob(reportFilename(args), buildResponseReport(args));
            }}
            className="mono rounded-md border border-mars-line bg-mars-charcoal/40 px-3 py-1.5 text-sm text-mars-white transition-colors hover:border-mars-orange disabled:cursor-not-allowed disabled:opacity-40"
          >
            Download report
          </button>
        </div>
      </header>

      <ExperimentStatusBar
        slug={quiz.slug}
        {...(quiz.experiment ? { experiment: quiz.experiment } : {})}
      />

      <section
        className={
          gated
            ? "mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5"
            : "mb-6 grid grid-cols-2 gap-4 md:grid-cols-4"
        }
      >
        <Stat
          label="Visitors"
          hint="Sessions that opened the quiz"
          value={formatNumber(data?.started ?? null)}
        />
        <Stat
          label="Submits"
          hint="Sessions that reached the result page"
          value={formatNumber(data?.completed ?? null)}
        />
        <Stat
          label="Time to Complete"
          hint="Avg. time across submits"
          value={formatDuration(data?.avg_duration_ms ?? null)}
        />
        <Stat
          label="Conversion Rate"
          hint="Submits ÷ Visitors"
          value={
            data?.completion_rate === null || data?.completion_rate === undefined
              ? "—"
              : `${data.completion_rate}%`
          }
          accent
        />
        {gated && (
          <Stat
            label="Emails Collected"
            hint="Leads this quiz asked for; sessions differ slightly"
            // Two different kinds of "missing" here, and they must render
            // differently. `!data` is "nothing has loaded yet" (matches the
            // other four cards' `?? null` -> formatNumber "—" convention).
            // `data.emails_captured ?? 0` is deploy skew (rule 23f): a real
            // response from an API deployed before this field existed, where
            // 0 is the correct fallback, not a loading state. Collapsing
            // both into `?? 0` (the previous code) showed "0" while the
            // FIRST load was still in flight, unlike every sibling stat.
            value={!data ? "—" : formatNumber(data.emails_captured ?? 0)}
          />
        )}
      </section>

      {error ? (
        <div className="rounded-2xl border border-red-900/60 bg-red-950/40 p-6 text-sm text-red-200">
          {error}
        </div>
      ) : (
        <>
          <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {loading || !data ? (
                <ChartSkeleton />
              ) : (
                <ErrorBoundary label="Traffic chart" resetKey={dataVersion}>
                  <TimeSeriesChart daily={data.daily} />
                </ErrorBoundary>
              )}
            </div>
            <div className="flex flex-col gap-4">
              <ErrorBoundary label="Conversion" resetKey={dataVersion}>
                <ConversionHero
                  rate={data?.completion_rate ?? null}
                  daily={data?.daily ?? []}
                  // Null, not the zeroed baseline the server sends for an arm.
                  // The hero hides the Purchase CVR block on null; rendering
                  // the zeros instead would state "0 orders" for a population
                  // whose orders were deliberately never measured (rule 25a).
                  cvrBaseline={armed ? null : (data?.cvr_baseline ?? null)}
                  cvrMinOrders={data?.cvr_min_orders ?? null}
                />
              </ErrorBoundary>
              <ErrorBoundary label="Devices" resetKey={dataVersion}>
                <DevicesCard
                  devices={
                    data?.devices ?? {
                      desktop: 0,
                      mobile: 0,
                      tablet: 0,
                      unknown: 0,
                    }
                  }
                />
              </ErrorBoundary>
            </div>
          </section>

          {data && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="eyebrow mr-1">Persona</span>
              <Chip
                active={persona === ""}
                onClick={() => setPersona("")}
                label="All"
              />
              {Object.entries(data.facets.persona_counts).map(([key, n]) => (
                <Chip
                  key={key}
                  active={persona === key}
                  onClick={() => setPersona(key)}
                  label={`${key} (${n})`}
                />
              ))}
            </div>
          )}

          {hasExperiment && (
            <div className="mb-4 flex items-center gap-2">
              <span className="eyebrow">Arm</span>
              <div className="inline-flex gap-1 rounded-xl border border-mars-line bg-mars-charcoal/40 p-1 text-sm">
                {ARMS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setArm(opt.value)}
                    className={
                      arm === opt.value
                        ? "mono rounded-lg bg-mars-orange px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-black"
                        : "mono rounded-lg px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-white/70 hover:text-mars-white"
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {armNotApplied && (
            <div
              className="mb-4 rounded-xl border border-amber-700/60 bg-amber-950/40 px-4 py-3 text-sm text-amber-200"
              data-testid="arm-not-applied"
            >
              {ARM_NOT_APPLIED}
            </div>
          )}

          {armed && !armNotApplied && (
            <div
              className="mb-4 rounded-xl border border-mars-line bg-mars-night/60 px-4 py-3 text-xs text-mars-white/60"
              data-testid="arm-scope-note"
            >
              {ARM_SCOPE_NOTE}
            </div>
          )}

          <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="eyebrow">Cohort</span>
              <div className="inline-flex gap-1 rounded-xl border border-mars-line bg-mars-charcoal/40 p-1 text-sm">
                {(["all", "completers", "abandoners"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setCohort(opt)}
                    className={
                      cohort === opt
                        ? "mono rounded-lg bg-mars-orange px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-black"
                        : "mono rounded-lg px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-white/70 hover:text-mars-white"
                    }
                  >
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="eyebrow">Denominator</span>
              <div className="inline-flex gap-1 rounded-xl border border-mars-line bg-mars-charcoal/40 p-1 text-sm">
                {(["answered", "started"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setDenominator(opt)}
                    className={
                      denominator === opt
                        ? "mono rounded-lg bg-mars-orange px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-black"
                        : "mono rounded-lg px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-white/70 hover:text-mars-white"
                    }
                  >
                    {`% of ${opt}`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading || !data ? (
            <TableSkeleton />
          ) : (
            // The panel most exposed to an API-shape change: every per-answer
            // field the breakdown response gained lands in this subtree.
            <ErrorBoundary label="Pages" resetKey={dataVersion}>
              <PagesTable
                started={data.started}
                completed={data.completed}
                steps={data.steps}
                questionLabels={labels}
                optionLabels={optionLabels}
                questionTypes={questionTypes}
                denominator={denominator}
                breakdowns={breakdowns}
                breakdownsLoading={breakdownsLoading}
                breakdownsUnavailableReason={armed ? ARM_SCOPE_NOTE : null}
                randomizedQuestionIds={randomizedQuestionIds}
                cvrBaseline={
                  data?.cvr_baseline ?? { sessions: 0, orders: 0, cvr: null }
                }
                // null, not a literal 30: a duplicated threshold would render
                // "n<30" on cells the server suppressed at some other number
                // if the constant ever moved. With null the label states no
                // number at all, which is the only honest fallback.
                cvrMinOrders={data?.cvr_min_orders ?? null}
                offerViewed={data.offer_viewed ?? 0}
                emailGateViewed={data.email_gate_viewed ?? 0}
                emailGateSubmitted={data.email_gate_submitted ?? 0}
                // From the quiz definition, not the data. A calculator has no
                // offer phase and never reaches the reducer's result phase, so
                // neither row means anything there.
                showOfferRow={quiz ? !isCalculatorQuiz(quiz) : false}
                showCompletionRow={quiz ? !isCalculatorQuiz(quiz) : true}
                showGateRows={gated}
              />
            </ErrorBoundary>
          )}
        </>
      )}
    </div>
  );
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function Stat({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={
        accent
          ? "rounded-2xl border border-mars-orange/40 bg-mars-orange/10 px-5 py-4"
          : "rounded-2xl border border-mars-line bg-mars-charcoal/40 px-5 py-4"
      }
    >
      <div className="eyebrow">{label}</div>
      <div
        className={
          accent
            ? "mono mt-2 text-3xl font-bold tabular-nums text-mars-orange"
            : "mono mt-2 text-3xl font-bold tabular-nums text-mars-white"
        }
      >
        {value}
      </div>
      {hint && (
        <div
          className={
            accent
              ? "mt-1.5 text-[0.7rem] text-mars-orange/70"
              : "mt-1.5 text-[0.7rem] text-mars-white/45"
          }
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "mono rounded-full border border-mars-orange bg-mars-orange/20 px-3 py-1 text-xs text-mars-orange"
          : "mono rounded-full border border-mars-line bg-mars-charcoal/40 px-3 py-1 text-xs text-mars-white/70 hover:border-mars-orange hover:text-mars-white"
      }
    >
      {label}
    </button>
  );
}

function ChartSkeleton() {
  return (
    <div className="h-[360px] animate-pulse rounded-2xl border border-mars-line bg-mars-charcoal/40" />
  );
}

function TableSkeleton() {
  return (
    <div className="h-[280px] animate-pulse rounded-2xl border border-mars-line bg-mars-charcoal/40" />
  );
}
